import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { 
  Car, 
  MapPin, 
  Building2, 
  QrCode,
  TrendingUp,
  AlertTriangle
} from "lucide-react";

export default function DashboardPage() {
  const { user, isGestorLocal } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const headerAction = (
    <Link to="/scanner">
      <Button className="bg-[#0047FF] hover:bg-[#0035CC]" data-testid="go-to-scanner-btn">
        <QrCode className="w-4 h-4 mr-2" />
        <span className="hidden sm:inline">Scanner</span>
      </Button>
    </Link>
  );

  return (
    <PageLayout title="Dashboard" subtitle="Visão geral do sistema" headerAction={headerAction}>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Alert for expired licenses (Gestor Local) */}
          {isGestorLocal && stats?.locais_expirados?.length > 0 && (
            <Alert variant="destructive" className="border-[#D50000] bg-[#D50000]/10" data-testid="expired-licenses-alert">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="font-bold">Locais com Licença Expirada</AlertTitle>
              <AlertDescription>
                <p className="mb-2">Os seguintes locais necessitam de renovação da licença:</p>
                <ul className="list-disc list-inside space-y-1">
                  {stats.locais_expirados.map((local) => (
                    <li key={local.local_id}>
                      <strong>{local.local_nome}</strong> - Expirou em {new Date(local.licenca_fim).toLocaleDateString('pt-PT')}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm">Contacte a administração para renovar as licenças.</p>
              </AlertDescription>
            </Alert>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-[#E2E8F0]" data-testid="stats-locais">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Locais</p>
                    <p className="text-3xl font-bold text-[#0F172A] mt-1">{stats?.total_locais || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#0047FF]/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#0047FF]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]" data-testid="stats-empresas">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Empresas</p>
                    <p className="text-3xl font-bold text-[#0F172A] mt-1">{stats?.total_empresas || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#00C853]/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#00C853]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]" data-testid="stats-viaturas">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Viaturas</p>
                    <p className="text-3xl font-bold text-[#0F172A] mt-1">{stats?.total_viaturas || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#FF9800]/10 rounded-lg flex items-center justify-center">
                    <Car className="w-6 h-6 text-[#FF9800]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]" data-testid="stats-dentro">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Dentro</p>
                    <p className="text-3xl font-bold text-[#0F172A] mt-1">{stats?.total_dentro || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#00C853]/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#00C853]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Locais Stats */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-[#0F172A]">Ocupação por Local</h2>
            
            {stats?.locais_stats?.length === 0 && (
              <Card className="border-[#E2E8F0]">
                <CardContent className="p-8 text-center">
                  <MapPin className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <p className="text-[#64748B]">Nenhum local configurado</p>
                  {user?.role === "super_admin" && (
                    <Link to="/locais">
                      <Button className="mt-4 bg-[#0047FF] hover:bg-[#0035CC]" data-testid="create-local-btn">
                        Criar Local
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}

            {stats?.locais_stats?.map((local) => (
              <Card 
                key={local.local_id} 
                className={`border-[#E2E8F0] ${local.licenca_expirada ? 'opacity-60' : ''}`} 
                data-testid={`local-card-${local.local_id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg font-bold text-[#0F172A]">{local.local_nome}</CardTitle>
                      {local.licenca_expirada && (
                        <Badge variant="destructive" className="bg-[#D50000]">
                          Licença Expirada
                        </Badge>
                      )}
                    </div>
                    {!local.licenca_expirada && (
                      <Badge 
                        variant={local.disponivel > 0 ? "default" : "destructive"}
                        className={local.disponivel > 0 ? "bg-[#00C853]" : "bg-[#D50000]"}
                      >
                        {local.disponivel > 0 ? `${local.disponivel} disponíveis` : "Lotado"}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Progress bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#64748B]">Ocupação</span>
                      <span className="font-medium text-[#0F172A]">
                        {local.ocupacao_atual} / {local.capacidade_total}
                      </span>
                    </div>
                    <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          local.licenca_expirada 
                            ? 'bg-[#64748B]'
                            : (local.ocupacao_atual / local.capacidade_total) > 0.9 
                              ? 'bg-[#D50000]' 
                              : (local.ocupacao_atual / local.capacidade_total) > 0.7 
                                ? 'bg-[#FF9800]' 
                                : 'bg-[#00C853]'
                        }`}
                        style={{ width: `${Math.min((local.ocupacao_atual / local.capacidade_total) * 100, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Empresas breakdown */}
                  {local.empresas?.length > 0 && !local.licenca_expirada && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Por Empresa</p>
                      <div className="grid gap-2">
                        {local.empresas.map((empresa) => (
                          <div 
                            key={empresa.empresa_id}
                            className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg"
                          >
                            <span className="font-medium text-[#0F172A]">{empresa.empresa_nome}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-[#64748B]">
                                {empresa.ocupacao} / {empresa.quota_total}
                              </span>
                              <Badge 
                                variant={empresa.disponivel > 0 ? "secondary" : "destructive"}
                                className={empresa.disponivel > 0 ? "" : "bg-[#D50000]"}
                              >
                                {empresa.disponivel > 0 ? `${empresa.disponivel} livres` : "Cheio"}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </PageLayout>
  );
}
